import { cx } from "@emotion/css";
import Divider from "@mui/material/Divider";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextBlackButton } from "src/components/Default/LekUI/Button";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import Box from "src/components/Default/Main/Box";
import { Header } from "src/components/Main/ExamsBox/ExamsBox";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import FinishedExamsTable from "src/components/Table/FinishedExamsTable";
import UnfinishedExamsTable from "src/components/Table/UnfinishedExamsTable";
import { useCreateExam, useRemoveExams } from "src/hooks/useExams";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import GenerateExamIcon from "src/images/Icons/newIcons/fileClose.svg";
import ExamIcon from "src/images/Icons/newIcons/navigation/exams.svg";
import SearchIcon from "src/images/Icons/newIcons/search.svg";
import StarIcon from "src/images/Icons/newIcons/star.svg";

interface SelectedExam {
	id: string;
	[key: string]: unknown;
}

export const ExamsList = () => {
	const { t } = useTranslation();

	const [selectedTab, setSelectedTab] = useState(0);
	const [searchValue, setSearchValue] = useState("");
	const [selectedElements, setSelectedElements] = useState<SelectedExam[]>([]);
	const { mutateAsync: generateExam } = useCreateExam() as unknown as {
		mutateAsync: (payload: Record<string, unknown>) => Promise<unknown>;
	};
	const { mutateAsync: removeExamsMutation } = useRemoveExams() as unknown as {
		mutateAsync: (ids: string[]) => Promise<void>;
	};
	const { openModal } = useModals();

	const onRowSelected = (row: SelectedExam) => {
		if (selectedElements.some((element) => element.id === row.id)) {
			setSelectedElements(
				selectedElements.filter((element) => element.id !== row.id),
			);
		} else {
			setSelectedElements([...selectedElements, row]);
		}
	};

	const removeExams = () => {
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.exams.title", "Usunąć egzaminy?"),
			description: t(
				"modals.deleteItem.exams.description",
				"Wybrane egzaminy zostaną trwale usunięte razem z odpowiedziami i statystykami. Tej operacji nie można cofnąć.",
			),
			confirmText: t("modals.deleteItem.exams.confirm", "Usuń egzaminy"),
			itemLabel: t("modals.deleteItem.exams.count", {
				count: selectedElements.length,
			}),
			onDelete: () => removeExamsMutation(selectedElements.map(({ id }) => id)),
		});
	};

	const generateFromWrongAnswers = async () => {
		await generateExam({
			...(selectedTab === 0
				? { unfinishedExamsIds: selectedElements }
				: { finishedExamsIds: selectedElements }),
			additionalOption: {
				wrongAnswers: true,
			},
			name: `${t("defaultNames.wrongAnswers")} - ${dayjs(new Date()).format("DD-MM-YYYY")}`,
		});
	};

	const generateFromStarred = async () => {
		await generateExam({
			...(selectedTab === 0
				? { unfinishedExamsIds: selectedElements }
				: { finishedExamsIds: selectedElements }),
			additionalOption: {
				starred: true,
			},
			name: `${t("defaultNames.starred")} - ${dayjs(new Date()).format("DD-MM-YYYY")}`,
		});
	};

	const onSelectActions = () => {
		return [
			{
				title: t("menuActions.exams.generateExamFromWrongAnswers"),
				onClick: generateFromWrongAnswers,
				Icon: <GenerateExamIcon className="icon-small" />,
			},
			{
				title: t("menuActions.exams.generateFromStarredQuestions"),
				onClick: generateFromStarred,
				Icon: <StarIcon className="icon-small" />,
			},
			{
				title: t("menuActions.exams.remove"),
				onClick: removeExams,
				Icon: <DeleteIcon className="icon-small icon-error" />,
				itemTextClassName: "text-error",
			},
		];
	};

	const tabs = [
		{
			name: t("table.exam.tabs.unfinishedExams"),
			renderContent: () => (
				<UnfinishedExamsTable
					searchValue={searchValue}
					selectedElements={selectedElements}
					onRowSelected={onRowSelected}
					onSelectActions={onSelectActions}
					clearSelectedRows={() => setSelectedElements([])}
				/>
			),
		},
		{
			name: t("table.exam.tabs.finishedExams"),
			renderContent: () => (
				<FinishedExamsTable
					searchValue={searchValue}
					selectedElements={selectedElements}
					onRowSelected={onRowSelected}
					onSelectActions={onSelectActions}
					clearSelectedRows={() => setSelectedElements([])}
				/>
			),
		},
	];

	const renderAdditionalActions = () => {
		const disabled = !selectedElements.length;
		return (
			<div className="table-additional-actions">
				<span>
					{t("table.exam.actions.title")} (
					<label>{selectedElements.length}</label>):{" "}
				</span>
				<TextBlackButton
					startIcon={<StarIcon className="icon-small" />}
					disabled={disabled}
					onClick={generateFromStarred}
				>
					{t("table.exam.actions.generateExamFromStarred")}
				</TextBlackButton>
				<TextBlackButton
					startIcon={<GenerateExamIcon className="icon-small" />}
					disabled={disabled}
					onClick={generateFromWrongAnswers}
				>
					{t("table.exam.actions.generateExamFromWrongAnswers")}
				</TextBlackButton>
				<Divider orientation="vertical" variant="middle" flexItem />
				<TextBlackButton
					startIcon={<DeleteIcon className="icon-small" />}
					disabled={disabled}
					onClick={removeExams}
					className={cx({
						"icon-error": !disabled,
					})}
				>
					{t("table.exam.actions.removeExams")}
				</TextBlackButton>
			</div>
		);
	};

	const renderSearch = () => {
		return (
			<TextFieldGrey
				variant="filled"
				placeholder={t("commands.search")}
				value={searchValue}
				onChange={(e) => setSearchValue(e.target.value)}
				className="w-100 search--text-field"
				data-test-id="exams-search-input"
				slotProps={{
					input: {
						disableUnderline: true,
						startAdornment: (
							<SearchIcon className="icon-normal icon-black-80" />
						),
					},
				}}
			/>
		);
	};

	return (
		<div className="creator-container">
			<Box
				tabsClassName="basic-box--table-tabs"
				headerClassName="mb-0"
				Header={
					<div>
						<Header
							title={t("table.exam.title")}
							icon={<ExamIcon className={cx("icon-super-big icon-primary")} />}
							withBackButton
						/>
						{renderAdditionalActions()}
					</div>
				}
				tabs={tabs.map(({ name }) => name)}
				selectedTab={selectedTab}
				onTabsChange={(newValue) => {
					setSelectedElements([]);
					setSelectedTab(newValue);
				}}
				additionalTabsActions={renderSearch}
			>
				{tabs[selectedTab].renderContent()}
			</Box>
		</div>
	);
};
